import type { ReactNode } from "react";
import {
    getLocalStorageItem,
    getSessionStorageItem,
    parseJSONSafely,
    setLocalStorageItem,
    setSessionStorageItem,
} from "utils/service";
import ColumnResizeHandle from "./ColumnResizeHandle";

/*
    {
        label: 'Label in table header',
        key: 'unique_key_for_column',
        dto: (data, type, id) => {}, (dto for formatting column value. Returns string or DOM)
        shown: true (whether column is shown by default)
    }
*/
export interface TableColumn {
    label: string;
    key: string;
    dto?: (data: any, refreshAction: () => void) => React.ReactNode;
    shown?: boolean;
    sortable?: boolean;
    filterable?: boolean;
    filterOptions?: {
        preset?: "ternary" | "nullable";
        renderValue?: (v: any) => string;
        renderChipValue?: (v: any) => string;
    };
    headerRenderer?: () => React.ReactNode;
    cellStyle?: any;
}

/**
 * Per-column drag-to-resize configuration threaded from RecordTable into each
 * mui column. `width` is the persisted user-set pixel width (overrides the
 * type-aware min/max caps); the callbacks report a new width while dragging /
 * on release, and clear the override on double-click.
 */
export interface ColumnResizeConfig {
    width?: number;
    resizable?: boolean;
    onResize?: (columnKey: string, width: number, commit: boolean) => void;
    onResetWidth?: (columnKey: string) => void;
}

export const convertLegacyColumnToMuiColumn = (
    legacyColumn: TableColumn,
    tableData: any[],
    afterButtonAction: () => void,
    resizeOptions?: ColumnResizeConfig
) => {
    // Derive filter renderers locally instead of mutating the caller's column
    // config, which is shared with React state.
    let renderValue = legacyColumn.filterOptions?.renderValue;
    let renderChipValue = legacyColumn.filterOptions?.renderChipValue;

    if (legacyColumn.filterOptions?.preset === "ternary") {
        renderValue = (v: any) => {
            if (v == null || v === "") return "none";
            if (v) return "true";
            else return "false";
        };
        renderChipValue = (v: any) => {
            if (v == null || v === "") return `${legacyColumn.label}: none`;
            if (v) return `${legacyColumn.label}: true`;
            else return `${legacyColumn.label}: false`;
        };
    }

    if (legacyColumn.filterOptions?.preset === "nullable") {
        renderValue = (v: any) => {
            if (v == null || v === "") return "none";
            return v;
        };
        renderChipValue = (v: any) => {
            if (v == null || v === "") return `${legacyColumn.label}: none`;
            return v;
        };
    }

    // Resolve the effective per-cell inline style once: the column's default
    // cellStyle (type-aware min/max caps, carried as a nested `style` key) with
    // an explicit user-set drag-resize width merged on top. A user width wins
    // over the caps — explicit intent overrides the default clamp — and is
    // applied as a fixed width (width == minWidth == maxWidth) so the column
    // holds the chosen size regardless of content.
    const cellStyle = legacyColumn.cellStyle ?? {};
    const { style: nestedCellStyle, ...cellStyleRest } = cellStyle;
    const baseInnerStyle = nestedCellStyle ?? {};
    const width = resizeOptions?.width;
    const innerStyle =
        typeof width === "number"
            ? { ...baseInnerStyle, width, minWidth: width, maxWidth: width }
            : baseInnerStyle;
    const styleProps = Object.keys(innerStyle).length > 0 ? { style: innerStyle } : {};

    // Only inject the resize grip when the caller enabled resizing AND gave us a
    // width sink; otherwise preserve the prior header rendering (mui default, or
    // a caller-supplied headerRenderer) exactly.
    const onResize = resizeOptions?.onResize;
    const onResetWidth = resizeOptions?.onResetWidth;
    let customHeadLabelRender:
        | ((columnMeta?: { label?: string }) => ReactNode)
        | undefined = legacyColumn.headerRenderer;
    // The column's type-aware minimum width (its cellStyle floor), used to clamp
    // the drag so a column can't be shrunk below the intrinsic size of the
    // control it renders (e.g. a true/false toggle) — otherwise the content
    // spills past the cell border into the neighbouring column
    // (operator-task-fe-resizable-column-narrow-width-behavior). A user-set width
    // (which replaces baseInnerStyle with a fixed width==min==max) never removes
    // this floor because it is read from baseInnerStyle, not the effective style.
    const columnDragMinWidth =
        typeof baseInnerStyle.minWidth === "number" ? baseInnerStyle.minWidth : undefined;
    if (resizeOptions?.resizable && onResize) {
        customHeadLabelRender = (columnMeta?: { label?: string }) => (
            <span className="recordTableHeadLabel">
                <span className="recordTableHeadLabelText">
                    {legacyColumn.headerRenderer
                        ? legacyColumn.headerRenderer()
                        : columnMeta?.label ?? legacyColumn.label}
                </span>
                <ColumnResizeHandle
                    columnKey={legacyColumn.key}
                    onResize={onResize}
                    onResetWidth={onResetWidth}
                    minWidth={columnDragMinWidth}
                />
            </span>
        );
    }

    return {
        name: legacyColumn.key,
        label: legacyColumn.label,
        options: {
            display: legacyColumn.shown ?? false,
            sort: legacyColumn.sortable ?? false,
            sortThirdClickReset: true,
            filter: legacyColumn.filterable === false ? false : true,
            customBodyRenderLite: (dataIndex: number, rowIndex: number) => {
                const value = tableData[dataIndex];
                if (legacyColumn.dto) return legacyColumn.dto(value, afterButtonAction);
                if (value == null || typeof value === "string" || typeof value === "number") {
                    return value ?? "";
                }
                return JSON.stringify(value);
            },
            setCellProps: (valuecellValue: string, rowIndex: number, columnIndex: number) => {
                return {
                    className: `${legacyColumn.key}Cell`,
                    ...cellStyleRest,
                    ...styleProps,
                };
            },
            setCellHeaderProps: (columnMeta: object) => {
                // Apply the column width (cap or user override) to the HEADER
                // cell too, so the constraint takes effect symmetrically. In
                // native table auto-layout a column's width is the max over all
                // its cells (header included); if only the body carried the
                // constraint a long header label could still force the column
                // wider than its cap.
                return {
                    className: `${legacyColumn.key}HeaderCell`,
                    ...cellStyleRest,
                    ...styleProps,
                };
            },
            filterOptions: {
                renderValue,
            },
            customFilterListOptions: {
                render: renderChipValue,
            },
            customHeadLabelRender,
        },
    };
};

export type TableDensity = "high" | "medium" | "regular";

export const getTableSettings = (
    tableSettingsKey: string,
    setting: "columns" | "limit" | "order" | "density" | "widths",
    defaultValue?: any
) => {
    const tableSettingsState = getLocalStorageItem(tableSettingsKey);
    return parseJSONSafely(tableSettingsState)?.[setting] ?? defaultValue;
};

/**
 * Read the persisted per-table row density, falling back to the caller-provided
 * default when nothing valid is stored. Density is a purely presentational,
 * per-table user preference (like the column selection and page size) persisted
 * under the table's settings key.
 */
export const getInitialDensity = (
    tableSettingsKey: string,
    fallback: TableDensity
): TableDensity => {
    const stored = getTableSettings(tableSettingsKey, "density");
    if (stored === "high" || stored === "medium" || stored === "regular") {
        return stored;
    }
    return fallback;
};

/**
 * Read the persisted per-column pixel widths (drag-to-resize overrides) for a
 * table, keyed by column key. Returns an empty map when nothing valid is
 * stored. Like density and column selection, widths are a per-table user
 * preference persisted under the table's settings key.
 */
export const getInitialColumnWidths = (tableSettingsKey: string): Record<string, number> => {
    const stored = getTableSettings(tableSettingsKey, "widths");
    if (!stored || typeof stored !== "object") return {};
    const widths: Record<string, number> = {};
    Object.entries(stored as Record<string, unknown>).forEach(([key, value]) => {
        if (typeof value === "number" && Number.isFinite(value) && value > 0) {
            widths[key] = value;
        }
    });
    return widths;
};

export const getInitialQueryWithTableSettings = (
    tableSettingsKey: string,
    initialQuery: { limit: any; order: string; offset: number }
) => {
    const tableSettingLimit = getTableSettings(tableSettingsKey, "limit");
    const tableSettingOrder = getTableSettings(tableSettingsKey, "order");
    const queryWithTableSettings = {
        ...initialQuery,
        limit: tableSettingLimit || initialQuery.limit,
        order: tableSettingOrder || initialQuery.order,
    };
    return queryWithTableSettings;
};

export const updateLocalStorageTableSettings = (
    tableSettingsKey: string,
    newData: Partial<Record<"columns" | "limit" | "order" | "density" | "widths", any>>
) => {
    if (tableSettingsKey) {
        const currentTableSettingsState = getLocalStorageItem(tableSettingsKey);
        const currentTableSettingsData = parseJSONSafely(currentTableSettingsState, {});
        const mergedTableSettings = { ...currentTableSettingsData, ...newData };
        setLocalStorageItem(tableSettingsKey, mergedTableSettings);
    }
};

/**
 * Reset the persisted column selection for a table back to its defaults by
 * dropping the stored `columns` override (page size / sort order are kept).
 * After this, getInitialTableColumns falls back to each column's default
 * `shown` value.
 */
export const resetLocalStorageTableColumns = (tableSettingsKey: string) => {
    if (tableSettingsKey) {
        const currentTableSettingsState = getLocalStorageItem(tableSettingsKey);
        const currentTableSettingsData = parseJSONSafely(currentTableSettingsState, {});
        delete currentTableSettingsData.columns;
        setLocalStorageItem(tableSettingsKey, currentTableSettingsData);
    }
};

export const getInitialTableColumns = (columns: TableColumn[], tableSettingsKey: string) => {
    let tableColumnsFromStorage: TableColumn[] = [];

    if (tableSettingsKey) {
        const savedColumnsFromStorage = getTableSettings(tableSettingsKey, "columns", []);
        const columnsFromStorage = savedColumnsFromStorage.reduce(
            (acc: any, column: any) => ({ ...acc, [column.key]: column }),
            {}
        );
        tableColumnsFromStorage = columns.map((column: any) => ({
            ...column,
            shown: columnsFromStorage[column.key]?.shown ?? column.shown,
        }));
    }

    return tableColumnsFromStorage?.length ? tableColumnsFromStorage : columns;
};

export const getSortOrder = (order: string): { name: string; direction: "asc" | "desc" } => {
    return {
        name: order.slice(1),
        direction: order.slice(0, 1) === "+" ? "asc" : "desc",
    };
};

type SessionSegmentPageFile = {
    segments: Record<string, number>;
};

/** Client-side table page per segment; cleared when the browser session ends. */
export const readSessionSegmentPage = (storageKey: string, segmentKey: string): number => {
    if (!storageKey) return 0;
    const raw = getSessionStorageItem(storageKey);
    const parsed = parseJSONSafely(raw, null) as Partial<SessionSegmentPageFile> | null;
    const page = parsed?.segments?.[segmentKey];
    return typeof page === "number" ? page : 0;
};

export const writeSessionSegmentPage = (storageKey: string, segmentKey: string, page: number) => {
    if (!storageKey) return;
    const raw = getSessionStorageItem(storageKey);
    const parsed = (parseJSONSafely(raw, null) as Partial<SessionSegmentPageFile> | null) ?? {};
    const next: SessionSegmentPageFile = {
        segments: { ...(parsed.segments ?? {}), [segmentKey]: page },
    };
    setSessionStorageItem(storageKey, next);
};
